import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Emits when a click lands outside the host element, or when Escape is pressed.
 *
 * The application reimplements this by hand in ~37 components via
 * `@HostListener('document:click')`. Prefer this directive in new code: it only listens while
 * enabled, understands shadow boundaries via `composedPath()`, adds Escape-to-close (which the
 * hand-rolled versions generally lack), and tears its listeners down on destroy.
 *
 * Apply it to the element that should count as "inside" — typically the dropdown wrapper, not the
 * whole component. Note that a selector-based `target.closest('.foo')` check over a *repeated*
 * element (one `.actions` cell per table row) is not equivalent to this directive on a single
 * host; such call sites need the directive on each repeated element, or should keep their
 * existing handler.
 *
 * @example
 *   <div class="dropdown-container"
 *        appClickOutside
 *        [appClickOutsideEnabled]="showDropdown"
 *        (appClickOutside)="showDropdown = false">
 *   </div>
 *
 * @input {boolean} appClickOutsideEnabled - Listen only while true. Defaults to true. Set it to
 *   the "is open" flag to avoid a document-wide listener while the target is closed.
 * @input {string} appClickOutsideIgnore - Optional selector; clicks inside a matching ancestor
 *   also count as "inside" (e.g. a dropdown panel rendered outside the host in an overlay).
 * @input {boolean} appClickOutsideEscape - Whether Escape also emits. Defaults to true.
 * @output {EventEmitter<void>} appClickOutside - A qualifying outside interaction occurred.
 */
@Directive({
  selector: '[appClickOutside]',
  standalone: true,
})
export class ClickOutsideDirective implements OnInit, OnDestroy {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject<Document>(DOCUMENT);

  private unlistenClick: (() => void) | null = null;
  private unlistenKeydown: (() => void) | null = null;
  private initialised = false;
  private enabled = true;

  @Input()
  set appClickOutsideEnabled(enabled: boolean) {
    this.enabled = enabled;
    // Before ngOnInit the flag is only recorded; ngOnInit decides whether to attach, so a
    // consumer binding `false` never causes a pointless attach/detach cycle.
    if (!this.initialised) {
      return;
    }
    if (enabled) {
      this.attach();
    } else {
      this.detach();
    }
  }

  @Input() appClickOutsideIgnore = '';
  @Input() appClickOutsideEscape = true;

  @Output() appClickOutside = new EventEmitter<void>();

  ngOnInit(): void {
    this.initialised = true;
    if (this.enabled) {
      this.attach();
    }
  }

  ngOnDestroy(): void {
    this.detach();
  }

  private attach(): void {
    if (this.unlistenClick) {
      return;
    }
    this.unlistenClick = this.renderer.listen(this.document, 'click', (event: Event) =>
      this.onDocumentClick(event)
    );
    this.unlistenKeydown = this.renderer.listen(this.document, 'keydown', (event: KeyboardEvent) =>
      this.onKeydown(event)
    );
  }

  private detach(): void {
    this.unlistenClick?.();
    this.unlistenKeydown?.();
    this.unlistenClick = null;
    this.unlistenKeydown = null;
  }

  private onDocumentClick(event: Event): void {
    if (!this.enabled || this.isInside(event)) {
      return;
    }
    this.appClickOutside.emit();
  }

  private onKeydown(event: KeyboardEvent): void {
    if (this.enabled && this.appClickOutsideEscape && event.key === 'Escape') {
      this.appClickOutside.emit();
    }
  }

  /** True when the event originated within the host, or within an explicitly ignored ancestor. */
  private isInside(event: Event): boolean {
    const hostElement = this.host.nativeElement;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    if (path.length > 0) {
      if (path.includes(hostElement)) {
        return true;
      }
    } else if (hostElement.contains(event.target as Node)) {
      return true;
    }

    if (!this.appClickOutsideIgnore) {
      return false;
    }

    const { target } = event;
    return target instanceof Element && !!target.closest(this.appClickOutsideIgnore);
  }
}
